/**
 * Resource Store
 * Manages stored resources (images, etc.) with IndexedDB persistence
 */

import { create } from 'zustand';
import { nanoid } from 'nanoid';
import type { Resource, ResourceCategory, CreateResourceInput, BuiltinResource } from '@/types/resource';

// Built-in resources shipped with the app
const BUILTIN_RESOURCES: BuiltinResource[] = [
  // Brand Assets
  {
    id: 'brand-mooncloak-logo',
    name: 'Mooncloak Logo',
    category: 'icons',
    src: '/images/mooncloak_logo_512.png',
    width: 512,
    height: 512,
  },
  {
    id: 'brand-mooncloak-wordmark',
    name: 'Mooncloak Wordmark (Light)',
    category: 'icons',
    src: '/images/resources/brand-mooncloak-wordmark.png',
    width: 618,
    height: 125,
  },
  {
    id: 'brand-mooncloak-wordmark-dark',
    name: 'Mooncloak Wordmark (Dark)',
    category: 'icons',
    src: '/images/resources/brand-mooncloak-wordmark-dark.png',
    width: 618,
    height: 125,
  },
  // App Store Badges
  {
    id: 'badge-app-store',
    name: 'App Store',
    category: 'badges',
    src: '/images/resources/badge-app-store.svg',
    width: 120,
    height: 40,
  },
  {
    id: 'badge-google-play',
    name: 'Google Play',
    category: 'badges',
    src: '/images/resources/badge-google-play.svg',
    width: 135,
    height: 40,
  },
  {
    id: 'badge-5-stars',
    name: '5 Star Rating',
    category: 'badges',
    src: '/images/resources/badge-5-stars.svg',
    width: 120,
    height: 24,
  },
  {
    id: 'badge-4-stars',
    name: '4 Star Rating',
    category: 'badges',
    src: '/images/resources/badge-4-stars.svg',
    width: 120,
    height: 24,
  },
  {
    id: 'badge-secure',
    name: 'Secure Badge',
    category: 'badges',
    src: '/images/resources/badge-secure.svg',
    width: 100,
    height: 40,
  },
  {
    id: 'badge-guarantee',
    name: 'Money Back Guarantee',
    category: 'badges',
    src: '/images/resources/badge-guarantee.svg',
    width: 100,
    height: 40,
  },
  {
    id: 'badge-verified',
    name: 'Verified',
    category: 'badges',
    src: '/images/resources/badge-verified.svg',
    width: 80,
    height: 80,
  },
  {
    id: 'badge-featured',
    name: 'Featured Badge',
    category: 'badges',
    src: '/images/resources/badge-featured.svg',
    width: 120,
    height: 40,
  },
  // Device Mockups
  {
    id: 'device-iphone-frame',
    name: 'iPhone Frame',
    category: 'devices',
    src: '/images/resources/device-iphone.svg',
    width: 300,
    height: 600,
  },
  {
    id: 'device-android-frame',
    name: 'Android Frame',
    category: 'devices',
    src: '/images/resources/device-android.svg',
    width: 300,
    height: 620,
  },
  {
    id: 'device-ipad',
    name: 'iPad Frame',
    category: 'devices',
    src: '/images/resources/device-ipad.svg',
    width: 400,
    height: 540,
  },
  {
    id: 'device-macbook',
    name: 'MacBook Frame',
    category: 'devices',
    src: '/images/resources/device-macbook.svg',
    width: 600,
    height: 380,
  },
  {
    id: 'device-watch',
    name: 'Apple Watch Frame',
    category: 'devices',
    src: '/images/resources/device-watch.svg',
    width: 180,
    height: 220,
  },
  {
    id: 'device-browser',
    name: 'Browser Window',
    category: 'devices',
    src: '/images/resources/device-browser.svg',
    width: 600,
    height: 400,
  },
  // Social Media Icons
  {
    id: 'social-facebook',
    name: 'Facebook',
    category: 'social',
    src: '/images/resources/social-facebook.svg',
    width: 48,
    height: 48,
  },
  {
    id: 'social-instagram',
    name: 'Instagram',
    category: 'social',
    src: '/images/resources/social-instagram.svg',
    width: 48,
    height: 48,
  },
  {
    id: 'social-tiktok',
    name: 'TikTok',
    category: 'social',
    src: '/images/resources/social-tiktok.svg',
    width: 48,
    height: 48,
  },
  {
    id: 'social-x',
    name: 'X (Twitter)',
    category: 'social',
    src: '/images/resources/social-x.svg',
    width: 48,
    height: 48,
  },
  {
    id: 'social-youtube',
    name: 'YouTube',
    category: 'social',
    src: '/images/resources/social-youtube.svg',
    width: 48,
    height: 48,
  },
  {
    id: 'social-linkedin',
    name: 'LinkedIn',
    category: 'social',
    src: '/images/resources/social-linkedin.svg',
    width: 48,
    height: 48,
  },
  {
    id: 'social-snapchat',
    name: 'Snapchat',
    category: 'social',
    src: '/images/resources/social-snapchat.svg',
    width: 48,
    height: 48,
  },
  {
    id: 'social-pinterest',
    name: 'Pinterest',
    category: 'social',
    src: '/images/resources/social-pinterest.svg',
    width: 48,
    height: 48,
  },
  {
    id: 'social-discord',
    name: 'Discord',
    category: 'social',
    src: '/images/resources/social-discord.svg',
    width: 48,
    height: 48,
  },
  {
    id: 'social-threads',
    name: 'Threads',
    category: 'social',
    src: '/images/resources/social-threads.svg',
    width: 48,
    height: 48,
  },
  // Decorative Elements
  {
    id: 'deco-arrow-right',
    name: 'Arrow Right',
    category: 'decorative',
    src: '/images/resources/deco-arrow-right.svg',
    width: 60,
    height: 40,
  },
  {
    id: 'deco-arrow-curved',
    name: 'Curved Arrow',
    category: 'decorative',
    src: '/images/resources/deco-arrow-curved.svg',
    width: 80,
    height: 60,
  },
  {
    id: 'deco-starburst',
    name: 'Starburst',
    category: 'decorative',
    src: '/images/resources/deco-starburst.svg',
    width: 100,
    height: 100,
  },
  {
    id: 'deco-ribbon',
    name: 'Banner Ribbon',
    category: 'decorative',
    src: '/images/resources/deco-ribbon.svg',
    width: 120,
    height: 40,
  },
  {
    id: 'deco-corner-flourish',
    name: 'Corner Flourish',
    category: 'decorative',
    src: '/images/resources/deco-corner-flourish.svg',
    width: 80,
    height: 80,
  },
  {
    id: 'deco-underline',
    name: 'Decorative Underline',
    category: 'decorative',
    src: '/images/resources/deco-underline.svg',
    width: 100,
    height: 20,
  },
  {
    id: 'deco-circle-badge',
    name: 'Circle Badge',
    category: 'decorative',
    src: '/images/resources/deco-circle-badge.svg',
    width: 80,
    height: 80,
  },
  {
    id: 'deco-confetti',
    name: 'Confetti',
    category: 'decorative',
    src: '/images/resources/deco-confetti.svg',
    width: 100,
    height: 100,
  },
  // CTA Buttons
  {
    id: 'btn-shop-now',
    name: 'Shop Now Button',
    category: 'buttons',
    src: '/images/resources/btn-shop-now.svg',
    width: 140,
    height: 50,
  },
  {
    id: 'btn-learn-more',
    name: 'Learn More Button',
    category: 'buttons',
    src: '/images/resources/btn-learn-more.svg',
    width: 140,
    height: 50,
  },
  {
    id: 'btn-get-started',
    name: 'Get Started Button',
    category: 'buttons',
    src: '/images/resources/btn-get-started.svg',
    width: 150,
    height: 50,
  },
  {
    id: 'btn-download',
    name: 'Download Button',
    category: 'buttons',
    src: '/images/resources/btn-download.svg',
    width: 140,
    height: 50,
  },
  {
    id: 'btn-sign-up',
    name: 'Sign Up Button',
    category: 'buttons',
    src: '/images/resources/btn-sign-up.svg',
    width: 140,
    height: 50,
  },
  {
    id: 'btn-try-free',
    name: 'Try Free Button',
    category: 'buttons',
    src: '/images/resources/btn-try-free.svg',
    width: 140,
    height: 50,
  },
];

// IndexedDB helpers
const DB_NAME = 'adstudio-resources';
const DB_VERSION = 1;
const STORE_NAME = 'resources';

async function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
  });
}

async function getAllResources(): Promise<Resource[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

async function saveResource(resource: Resource): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(resource);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

async function deleteResourceFromDB(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

// Convert builtin resource to full Resource format
function builtinToResource(builtin: BuiltinResource): Resource {
  return {
    id: builtin.id,
    name: builtin.name,
    type: 'image',
    category: builtin.category,
    data: {
      src: builtin.src,
      width: builtin.width,
      height: builtin.height,
      mimeType: builtin.src.endsWith('.svg') ? 'image/svg+xml' : 'image/png',
    },
    isBuiltin: true,
    createdAt: '2024-01-01T00:00:00.000Z',
    updatedAt: '2024-01-01T00:00:00.000Z',
  };
}

interface ResourceState {
  resources: Resource[];
  isLoading: boolean;
  selectedCategory: ResourceCategory | 'all';
}

interface ResourceActions {
  loadResources: () => Promise<void>;
  addResource: (input: CreateResourceInput) => Promise<Resource>;
  deleteResource: (id: string) => Promise<void>;
  setSelectedCategory: (category: ResourceCategory | 'all') => void;
  getResourcesByCategory: (category: ResourceCategory | 'all') => Resource[];
}

export const useResourceStore = create<ResourceState & ResourceActions>()((set, get) => ({
  resources: [],
  isLoading: true,
  selectedCategory: 'all',

  loadResources: async () => {
    set({ isLoading: true });
    try {
      // Load user resources from IndexedDB
      const userResources = await getAllResources();

      // Combine with builtin resources
      const builtinResources = BUILTIN_RESOURCES.map(builtinToResource);

      set({
        resources: [...builtinResources, ...userResources],
        isLoading: false,
      });
    } catch (error) {
      console.error('Failed to load resources:', error);
      // Fall back to just builtin resources
      set({
        resources: BUILTIN_RESOURCES.map(builtinToResource),
        isLoading: false,
      });
    }
  },

  addResource: async (input) => {
    const now = new Date().toISOString();
    const resource: Resource = {
      ...input,
      id: nanoid(),
      createdAt: now,
      updatedAt: now,
    };

    // Save to IndexedDB
    await saveResource(resource);

    // Update state
    set((state) => ({
      resources: [...state.resources, resource],
    }));

    return resource;
  },

  deleteResource: async (id) => {
    const resource = get().resources.find((r) => r.id === id);
    if (!resource || resource.isBuiltin) return;

    // Delete from IndexedDB
    await deleteResourceFromDB(id);

    // Update state
    set((state) => ({
      resources: state.resources.filter((r) => r.id !== id),
    }));
  },

  setSelectedCategory: (category) => {
    set({ selectedCategory: category });
  },

  getResourcesByCategory: (category) => {
    const { resources } = get();
    if (category === 'all') return resources;
    return resources.filter((r) => r.category === category);
  },
}));

// Selector hooks
export function useResources() {
  return useResourceStore((s) => s.resources);
}

export function useResourcesByCategory(category: ResourceCategory | 'all') {
  const resources = useResourceStore((s) => s.resources);
  if (category === 'all') return resources;
  return resources.filter((r) => r.category === category);
}

export function useSelectedCategory() {
  return useResourceStore((s) => s.selectedCategory);
}
